import { type CollectTill } from "../__app.collect/collect-till-context";

import { type PreOrderLine } from "./orders-create-context";
import { lineDueInclVat } from "./price-pre-order-line";

type CollectionModule = CollectTill["config"]["collectionModules"][number];

/**
 * `balanceLimited` is what the positive balance must cover, `credit` is what the
 * basket draws from the wallet: a debt-enabled line moves the balance without
 * being permitted by it. Invoiced lines touch neither, and a line naming a
 * module the till does not serve is deliberately left ungated, mirroring
 * till-api's `findWalletDebtRejections` -- but this client's backend runs no
 * balance check of its own, so nothing downstream refuses such a line either.
 */
function totals(lines: PreOrderLine[], modules: CollectionModule[]) {
  const modulesById = new Map(modules.map((module) => [module.id, module]));

  return lines.reduce(
    (totals, line) => {
      const module = modulesById.get(line.moduleId);

      if (module?.paymentMethod !== "credit") return totals;

      const dueInclVat = lineDueInclVat(line);

      return {
        credit: totals.credit + dueInclVat,
        balanceLimited: module.allowsDebt
          ? totals.balanceLimited
          : totals.balanceLimited + dueInclVat
      };
    },
    { credit: 0, balanceLimited: 0 }
  );
}

/**
 * Null for covered rather than 0, so a basket needing no positive balance never
 * reports a shortfall on an already-negative wallet. The balance stays signed:
 * on a wallet at -200 a 100 line is short by 300, matching the server's
 * `remaining - credit < 0`.
 */
function shortfall(
  balanceInclVat: number | null,
  dueInclVat: number
): number | null {
  if (dueInclVat === 0) return null;

  const availableInclVat = balanceInclVat ?? 0;

  if (dueInclVat <= availableInclVat) return null;

  return dueInclVat - availableInclVat;
}

/** Priced against the basket as it would be with `candidate` taken. */
type ShortfallForCandidate = (candidate: PreOrderLine) => number | null;

/**
 * A wallet in another currency cannot fund this till, so it counts as empty
 * rather than being spent at face value.
 */
export function resolveBalanceInclVat(
  wallet: { currency?: string | null; balance?: number | null } | undefined,
  tillCurrency: string
): number | null {
  if (!wallet || wallet.currency !== tillCurrency) return null;

  return wallet.balance ?? null;
}

type BasketEvaluation = {
  shortfallInclVat: number | null;
  resultingBalanceInclVat: number | null;
  shortfallForCandidate: ShortfallForCandidate;
};

export function evaluateBasket(
  lines: PreOrderLine[],
  modules: CollectionModule[],
  balanceInclVat: number | null
): BasketEvaluation {
  const { credit, balanceLimited } = totals(lines, modules);

  const drawsOnWallet = balanceInclVat !== null && credit !== 0;

  return {
    shortfallInclVat: shortfall(balanceInclVat, balanceLimited),
    resultingBalanceInclVat: drawsOnWallet ? balanceInclVat - credit : null,
    shortfallForCandidate: (candidate) =>
      shortfall(
        balanceInclVat,
        totals(
          [
            ...lines.filter((line) => line.moduleId !== candidate.moduleId),
            candidate
          ],
          modules
        ).balanceLimited
      )
  };
}
